import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * MEMORIA DE SCROLL GLOBAL (queja masiva del equipo: navegas a un claim, das "atrás" y la lista
 * vuelve ARRIBA — pierdes dónde ibas). Guarda el scroll del window POR URL (pathname+search) en
 * sessionStorage y lo restaura al volver. Como el contenido carga async (listas, fotos), reintenta
 * el scroll hasta que el documento tenga la altura suficiente (~3s) y se CANCELA al instante si el
 * usuario scrollea a mano. Los deep-links con #ancla tienen prioridad (no se restaura).
 */
export function ScrollMemory(): null {
  const { pathname, search, hash } = useLocation();
  const key = `scroll:${pathname}${search}`;

  // Guardado continuo (throttle) del scroll de ESTA url.
  useEffect(() => {
    let t: number | null = null;
    const onScroll = () => {
      if (t != null) return;
      t = window.setTimeout(() => {
        t = null;
        try { sessionStorage.setItem(key, String(Math.round(window.scrollY))); } catch { /* */ }
      }, 150);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (t != null) clearTimeout(t); };
  }, [key]);

  // Restaura al montar/cambiar de url (si hay algo guardado y no hay #ancla).
  useEffect(() => {
    if (hash) return; // deep-link (#msg-… / #case-overview): manda el ancla, no la memoria
    let saved = NaN;
    try { saved = Number(sessionStorage.getItem(key) ?? NaN); } catch { /* */ }
    if (!Number.isFinite(saved) || saved <= 0) return;
    let stopped = false;
    let tries = 0;
    const cancel = () => {
      stopped = true;
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchmove', cancel);
      window.removeEventListener('keydown', cancel);
    };
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchmove', cancel, { passive: true });
    window.addEventListener('keydown', cancel);
    const iv = setInterval(() => {
      if (stopped || tries++ > 20) { clearInterval(iv); cancel(); return; }
      // Solo scrollea cuando el contenido ya alcanzó la altura (las listas cargan async).
      if (document.documentElement.scrollHeight >= saved + window.innerHeight * 0.5) {
        window.scrollTo(0, saved);
        // Un par de ticks extra por si el layout sigue empujando (fotos), luego se apaga.
        if (Math.abs(window.scrollY - saved) < 4 && tries > 2) { clearInterval(iv); cancel(); }
      }
    }, 150);
    return () => { clearInterval(iv); cancel(); };
  }, [key, hash]);

  return null;
}
